import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, SparklesIcon, UserGroupIcon, CalendarIcon, BuildingStorefrontIcon } from '@heroicons/react/24/outline';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-pri-dark to-sec-dark text-white">
      {/* Header with navigation back */}
      <header className="bg-pri-dark/90 backdrop-blur-md fixed top-0 w-full z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              className="h-10 w-auto"
              src={`${process.env.REACT_APP_CDN_URL}/logo/logo-no-bg.png`}
              alt="Jingleez"
            />
          </Link>
          
          <Link
            to="/"
            className="inline-flex items-center text-sm font-semibold rounded-full px-4 py-2 bg-white/10 hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-soft-pink">About Us</h1>
            <div className="h-1 w-24 bg-mint-green mx-auto mb-6"></div>
            <p className="text-pri-gray text-lg max-w-2xl mx-auto">
              Learn about our mission and the story behind <span className="text-soft-pink">Jingleez</span>
            </p>
          </motion.div>

          <div className="space-y-16">
            {/* Our Story */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-pri-dark rounded-xl shadow-md p-8 md:p-10"
            >
              <div className="flex flex-col md:flex-row items-start gap-10">
                <div className="md:w-[45%]">
                  <h2 className="text-3xl font-bold mb-2 text-white">Our Story</h2>
                  <div className="h-0.5 w-24 bg-soft-pink mb-5"></div>
                  <div className="space-y-5">
                    <p className="text-pri-gray/90">
                    In today’s digital world, we often find ourselves juggling multiple apps and platforms just to get through everyday tasks. 
                    </p>
                    <p className="text-pri-gray/90">
                      We asked ourselves: "Why can't there be one platform that brings everything together?"
                    </p>
                    <p className="text-pri-gray/90">
                      That question led to the creation of <span className="text-soft-pink">Jingleez</span> 
                    </p>
                  </div>
                </div>
                <div className="md:w-[55%]">
                  <div className="rounded-lg overflow-hidden">
                    <img 
                      src={`${process.env.REACT_APP_CDN_URL}/illustrations/about-us.png`}
                      alt="About Jingleez" 
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Why We're Building Jingleez */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 rounded-2xl p-8 border border-white/10 shadow-lg"
            >
              <h2 className="text-3xl font-bold mb-4">Why We're Building <span className="text-soft-pink">Jingleez</span></h2>
              <div className="h-1 w-16 bg-mint-green mb-6"></div>
              <div className="mt-8 p-6 bg-white/5 rounded-xl border border-white/10">
                <p className="text-pri-gray italic">
                  "We’re creating Jingleez with one belief at heart: technology should serve people — not the other way around. Our mission is to build a unified digital ecosystem that genuinely enriches the way people live and interact with their digital world."
                </p>
                <p className="text-mint-green font-medium mt-4">- Jingleez Team</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
                <div className="flex flex-col items-center text-center">
                  <div className="h-16 w-16 rounded-full bg-mint-green/10 flex items-center justify-center mb-4">
                    <CalendarIcon className="h-8 w-8 text-mint-green" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Digital Ecosystem</h3>
                  <p className="text-pri-gray">
                  We value your time. By unifying everything in one place, we aim to create a digital ecosystem that enriches how people engage with their digital lives.
                  </p>
                </div>
                
                <div className="flex flex-col items-center text-center">
                  <div className="h-16 w-16 rounded-full bg-mint-green/10 flex items-center justify-center mb-4">
                    <UserGroupIcon className="h-8 w-8 text-mint-green" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Technology for You</h3>
                  <p className="text-pri-gray">
                  Technology is for people. By recent advancements in technology, we believe we can make life easier for everyone.
                  </p>
                </div>
                
                <div className="flex flex-col items-center text-center">
                  <div className="h-16 w-16 rounded-full bg-mint-green/10 flex items-center justify-center mb-4">
                    <BuildingStorefrontIcon className="h-8 w-8 text-mint-green" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Community Focus</h3>
                  <p className="text-pri-gray">
                    We're committed to helping local businesses thrive by connecting them directly with customers in their communities through our integrated platform.
                  </p>
                </div>
              </div>
              
              
            </motion.div>

            {/* Our Values */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <h2 className="text-3xl font-bold mb-4 text-mint-green">Our Values</h2>
              <div className="h-1 w-16 bg-soft-pink mb-6"></div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white/5 p-6 rounded-xl border border-white/10">
                  <h3 className="text-xl font-bold mb-2 text-soft-pink">User-Centered Design</h3>
                  <p className="text-pri-gray">
                    We build every feature with our users' needs at the center, focusing on intuitive experiences that feel natural and helpful.
                  </p>
                </div>
                
                <div className="bg-white/5 p-6 rounded-xl border border-white/10">
                  <h3 className="text-xl font-bold mb-2 text-soft-pink">Community First</h3>
                  <p className="text-pri-gray">
                    We believe in the power of local communities and design our platform to strengthen those connections.
                  </p>
                </div>
                
                <div className="bg-white/5 p-6 rounded-xl border border-white/10">
                  <h3 className="text-xl font-bold mb-2 text-soft-pink">Simplicity</h3>
                  <p className="text-pri-gray">
                    We strive to make complex problems simple, creating technology that's powerful yet easy to use.
                  </p>
                </div>
                
                <div className="bg-white/5 p-6 rounded-xl border border-white/10">
                  <h3 className="text-xl font-bold mb-2 text-soft-pink">Continuous Improvement</h3>
                  <p className="text-pri-gray">
                    We're always learning and improving, taking user feedback seriously to create a platform that truly serves your needs.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Join Us */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              className="text-center bg-gradient-to-r from-soft-pink/20 to-mint-green/20 rounded-2xl p-8 border border-white/10 shadow-lg"
            >
              <h2 className="text-3xl font-bold mb-4">Join Our Journey</h2>
              <p className="text-pri-gray mb-6 max-w-2xl mx-auto">
                We're in the early stages of our journey and currently in beta. We'd love for you to be part of our story and help shape the future of Jingleez.
              </p>
              <Link to="/signup">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 bg-mint-green text-pri-dark font-bold rounded-full"
                >
                  Join the Beta
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 mt-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-pri-gray">© {new Date().getFullYear()} Jingleez. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
} 